/**
 * The compare panel (collaboration phase 4, the design's "Compare before you lose anything"): a whole-file write (an
 * AI agent's, or a person's) went into a file while someone typed the same lines; the write won there. For each
 * overlap: the live lines, the writer's, and what is in the file now (merged); then Keep merged / Keep Luca's / Keep
 * the agent's (vfs-service `POST /collab/conflicts/:id/resolve`). Shown for 10 minutes, to everyone on the file.
 *
 * `conflicts`: presence's list (snapshot.conflicts) for this file. `onResolved(conflict, keep)` after a keep.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { resolveConflict } from '../../services/CollabService';
import { firstName } from '../../utils/presenceModel';
import './Collab.css';

/** "Luca's", "your" ... for the live side; "the agent's" or "Marta's" for the writer. */
export function sideLabels(conflict, you) {
  const live = conflict?.live || [];
  const mine = live.some((p) => you && p.sub && p.sub === you.sub);
  const liveName = mine ? 'yours' : live.length === 1 ? `${firstName(live[0].name)}’s` : 'the typed lines';
  const writer = conflict?.writer || {};
  const writerName = writer.kind === 'agent' ? 'the agent’s' : `${firstName(writer.name)}’s`;
  const liveTitle = mine ? 'You, live' : live.length ? `${live.map((p) => firstName(p.name)).join(', ')}, live` : 'Typed live';
  const writerTitle = writer.kind === 'agent' ? `${writer.label || 'AI agent'} · ${firstName(writer.name)}` : firstName(writer.name);
  return { liveName, writerName, liveTitle, writerTitle };
}

function Lines({ title, lines, tone }) {
  return (
    <div className={`collab-conflict-side is-${tone}`}>
      <div className="collab-conflict-side-title">{title}</div>
      <pre className="collab-conflict-code">{(lines || []).join('\n') || ' '}</pre>
    </div>
  );
}

Lines.propTypes = { lines: PropTypes.arrayOf(PropTypes.string), title: PropTypes.string, tone: PropTypes.string };

export default function ConflictPanel({ projectId, conflicts = [], you = null, canResolve = true, onResolved = null }) {
  const [busy, setBusy] = useState(null);
  if (!projectId || !conflicts.length) { return null; }
  const keep = async (conflict, side) => {
    setBusy(`${conflict.id}:${side}`);
    try {
      await resolveConflict(projectId, conflict.id, side);
      if (onResolved) { onResolved(conflict, side); }
    } catch (error) {
      message.error(error.message || 'Could not keep that version.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="collab-conflicts" data-testid="conflict-panel">
      {conflicts.map((conflict) => {
        const labels = sideLabels(conflict, you);
        const where = (conflict.lines || []).map(([a, b]) => (a === b ? `${a}` : `${a}–${b}`)).join(', ');
        return (
          <section aria-label={`Compare: lines ${where}`} className="collab-conflict" key={conflict.id}>
            <div className="collab-conflict-head">
              <strong>{labels.writerTitle} replaced {where.includes(',') || where.includes('–') ? `lines ${where}` : `line ${where}`}</strong>
              <span className="collab-muted collab-small"> where {labels.liveName === 'yours' ? 'you were' : `${labels.liveName.replace(/’s$/, '')} was`} typing. The file has the merged text; keep another version if you need it.</span>
            </div>
            {(conflict.hunks || []).map((hunk) => (
              <div className="collab-conflict-hunk" key={hunk.merged.join('-')}>
                <Lines lines={hunk.live} title={labels.liveTitle} tone="live" />
                <Lines lines={hunk.writer} title={labels.writerTitle} tone="writer" />
                <Lines lines={hunk.kept} title="Kept (merged)" tone="kept" />
              </div>
            ))}
            {canResolve && (
              <div className="collab-conflict-actions">
                <button className="collab-btn is-primary" disabled={Boolean(busy)} onClick={() => keep(conflict, 'merged')} type="button">Keep merged</button>
                <button className="collab-btn" disabled={Boolean(busy)} onClick={() => keep(conflict, 'live')} type="button">Keep {labels.liveName}</button>
                <button className="collab-btn" disabled={Boolean(busy)} onClick={() => keep(conflict, 'writer')} type="button">Keep {labels.writerName}</button>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

ConflictPanel.propTypes = {
  canResolve: PropTypes.bool,
  conflicts: PropTypes.arrayOf(PropTypes.object),
  onResolved: PropTypes.func,
  projectId: PropTypes.string,
  you: PropTypes.object,
};
